// The want page's state and writes (#222; spec
// docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md). One read
// of the page feeds both tabs, made with the group picker's Reach so the
// holders are the picked group's; the owner's writes (add / remove a card,
// re-point its Card Specificity, add / remove a Project) go through the
// want_list endpoint from here. `WantPage.svelte` renders this and starts a
// load for its handle and picker selection (`scopeKey`); `WantsTab` reads
// everything it shows from here, the cards added this visit and the want a
// re-point merged into (`survivorOf`) included. Re-points run through
// logic/repoint-queue.js, its merges kept in a reactive map so a view
// deriving from `survivorOf` hears of them.

import { SvelteMap } from 'svelte/reactivity';
import {
  fetchWantList,
  removeProject,
  addWantEntry,
  removeWantEntry,
  setEntrySpecificity,
  addProject,
} from '../services/wantlist-api.js';
import { activeSubgroupIds, selectedServerGroupId } from './browse.svelte.js';
import { isSignedIn } from './auth.svelte.js';
import { buildWantListUrl, projectParamOf, resolveView } from '../logic/wantlist-logic.js';
import {
  buildShelf,
  projectAddError,
  repointEntries,
  DEFAULT_BUNDLE_ID,
} from '../logic/want-page-logic.js';
import { createRepointQueue } from '../logic/repoint-queue.js';

/** @typedef {import('../logic/want-page-logic.js').ShelfProject} ShelfProject */
/** @typedef {import('../types.js').WantEntry} WantEntry */
/** @typedef {{ groupId?: string, subgroupIds?: string[] }} Scope */

/** The group picker's current selection as the read's scope: none for no group. @returns {Scope} */
function pickerScope() {
  const groupId = selectedServerGroupId();
  return groupId ? { groupId, subgroupIds: activeSubgroupIds() } : {};
}

/** @param {Scope} scope */
function scopeKeyOf(scope) {
  return scope.groupId ? `${scope.groupId}|${(scope.subgroupIds ?? []).join(',')}` : '';
}

class WantPageState {
  loading = $state(true);
  notFound = $state(false);
  // Signed out: the page asks to sign in and never reads (the GET needs a
  // Signed-in Viewer).
  signedOut = $state(false);
  // A reload failed: 'afterWrite' when it followed a write the server took
  // (the change is saved, the list shown is behind), 'reload' otherwise.
  reloadFailed = $state(/** @type {false | 'afterWrite' | 'reload'} */ (false));
  isSelf = $state(false);
  // Replaced whole on every load, never mutated: no deep proxies.
  entries = $state.raw(/** @type {WantEntry[]} */ ([]));
  projects = $state.raw(/** @type {ShelfProject[]} */ ([]));
  // CONTEXT.md "Project cap", as the server sends it with the projects.
  projectCap = $state(0);
  pickedId = $state(DEFAULT_BUNDLE_ID);
  // The Wants tab's cards as a list or as tiles.
  display = $state(/** @type {'list' | 'cards'} */ ('list'));
  // The picker's selection was refused (400 not a Reach the viewer has, 403
  // a group they are not in): People fails, Wants still shows.
  peopleFailed = $state(false);
  // The scope `entries` were read with.
  loadedScopeKey = $state('');
  // Cards added this visit, newest first: they lead the Default Want List
  // until a column is sorted. Read through `recentWants`.
  recentIds = $state.raw(/** @type {number[]} */ ([]));

  // The shelf: [defaultBundle, ...projects].
  bundles = $derived(buildShelf(this.entries, this.projects));
  // The picker's selection as a plain string: a new one means a new read.
  scopeKey = $derived(scopeKeyOf(pickerScope()));
  // While the read's scope is not the picker's, People's holders belong to
  // the previous selection (dimmed and aria-busy until the new read lands).
  peopleStale = $derived(this.loadedScopeKey !== this.scopeKey);
}

export const wantPage = new WantPageState();

// Not rendered, so plain module state. `urlHandle` is the handle the address
// bar shows (a self view's @me becomes the real handle); `loadedHandle` the
// one last read.
let urlHandle = '';
let loadedHandle = /** @type {string | null} */ (null);
let loadSeq = 0;
// Bumped by every reset: a write that lands after one belongs to a page that
// is gone, and leaves this one alone.
let session = 0;
const merges = new SvelteMap();
/** @type {ReturnType<typeof createRepointQueue<WantEntry>>} */
let queue = createRepointQueue({ merges });

/**
 * A fresh page, the one reset path: WantPage calls it on mount, and
 * App.svelte keys WantPage on the handle, so another owner's list is a new
 * mount. Nothing of a previous page, its loads in flight, its merges or its
 * adds carries over.
 */
export function resetWantPage() {
  session += 1;
  loadSeq += 1;
  loadedHandle = null;
  urlHandle = '';
  merges.clear();
  queue = createRepointQueue({ merges });
  Object.assign(wantPage, {
    loading: true,
    notFound: false,
    signedOut: false,
    reloadFailed: false,
    isSelf: false,
    entries: [],
    projects: [],
    projectCap: 0,
    pickedId: DEFAULT_BUNDLE_ID,
    display: 'list',
    peopleFailed: false,
    loadedScopeKey: '',
    recentIds: [],
  });
}

/**
 * The want a (possibly merged-away) want id now is: the open options follow
 * a re-point that merged their want into another. Reactive: a merge re-runs
 * whatever derived from it.
 * @param {number} id
 */
export function survivorOf(id) {
  return queue.survivorOf(id);
}

/** The cards added this visit, newest first, each as the want it now is. */
export function recentWants() {
  return [...new Set(wantPage.recentIds.map((id) => queue.survivorOf(id)))];
}

/** A chosen sort puts the new cards in their place. */
export function clearRecent() {
  wantPage.recentIds = [];
}

/**
 * Fetch a want list and land on the bundle it should show: `pick` names it
 * (a removal lands on the Default Want List); otherwise resolveView decides
 * (`?project=` on the first load), with the Default Want List as its
 * fallback.
 * The read carries the picker's Reach (`scope`, the current selection by
 * default); the latest load wins. A refused Reach (400, or 403 for a group
 * the viewer is no longer in) fails People only: the page reads again
 * without it so the Wants tab still shows. A reload that fails keeps the
 * list on screen and says so (`afterWrite`: the write behind it was saved);
 * only a first load that fails is "Not found". A signed-out Viewer is asked
 * to sign in and the page is never read (decided on the restored session).
 * @param {string} h
 * @param {{ pick?: string, scope?: Scope, afterWrite?: boolean }} [opts]
 */
export async function loadWantPage(h, { pick, scope = pickerScope(), afterWrite = false } = {}) {
  const mine = ++loadSeq;
  const firstLoad = h !== loadedHandle;
  if (firstLoad) urlHandle = h;
  wantPage.loading = firstLoad;
  wantPage.notFound = false;
  if (!(await isSignedIn())) {
    if (mine !== loadSeq) return;
    wantPage.signedOut = true;
    wantPage.loading = false;
    return;
  }
  wantPage.signedOut = false;
  let res = await fetchWantList(h, scope);
  const refused = !res.ok && (res.status === 400 || res.status === 403) && !!scope.groupId;
  if (refused) res = await fetchWantList(h);
  if (mine !== loadSeq) return; // a newer load is under way
  wantPage.peopleFailed = refused;
  if (!res.ok && !firstLoad) {
    wantPage.reloadFailed = afterWrite ? 'afterWrite' : 'reload';
  } else if (!res.ok) {
    Object.assign(wantPage, { notFound: true, entries: [], projects: [], isSelf: false });
  } else {
    const data = res.data;
    loadedHandle = h;
    Object.assign(wantPage, {
      reloadFailed: false,
      isSelf: !!data?.is_self,
      entries: data?.entries || [],
      loadedScopeKey: scopeKeyOf(scope),
      projects: data?.projects || [],
      projectCap: data?.project_cap ?? 0,
    });
    wantPage.pickedId = resolveView({
      select: pick,
      firstLoad,
      projects: wantPage.projects,
      search: window.location.search,
      current: wantPage.pickedId,
      fallback: DEFAULT_BUNDLE_ID,
    });
    const username = data?.username;
    if (wantPage.isSelf && username && (h === 'me' || h === '@me')) urlHandle = username;
    syncUrl();
  }
  wantPage.loading = false;
}

function syncUrl() {
  const project = wantPage.projects.find((p) => p.binder_id === wantPage.pickedId);
  history.replaceState(
    history.state,
    '',
    buildWantListUrl(urlHandle, { project: project ? projectParamOf(project) : null })
  );
}

/** @param {string} id */
export function pickBundle(id) {
  wantPage.pickedId = id;
  syncUrl();
}

/** Reload the page read after a write, with the picker's Reach. */
function reloadAfterWrite(opts = {}) {
  return loadWantPage(/** @type {string} */ (loadedHandle), { ...opts, afterWrite: true });
}

/** @param {string} projectBinderId */
async function onRemoveProject(projectBinderId) {
  const mine = session;
  const res = await removeProject(projectBinderId);
  if (res.ok && mine === session) await reloadAfterWrite({ pick: DEFAULT_BUNDLE_ID });
}

/**
 * Add a typed or suggested card to the Default Want List (a manual add: the
 * server canonicalizes the name; CONTEXT.md "Want List Entry"). The POST
 * answers only the new id, so the list is reloaded for its Sample Card; the
 * new card then leads the list. Resolves to the new entry's id, or null when
 * the add failed.
 * @param {string} name
 * @returns {Promise<number | null>}
 */
async function onAddCard(name) {
  const mine = session;
  const res = await addWantEntry({ card_name: name, source: 'manual' });
  if (!res.ok || res.data?.id == null || mine !== session) return null;
  const id = res.data.id;
  await reloadAfterWrite();
  wantPage.recentIds = [id, ...wantPage.recentIds.filter((r) => r !== id)];
  return id;
}

/**
 * Remove one want at once, and put just that one back if the server refuses
 * (anything else that changed meanwhile stays as it is).
 * @param {number} id
 */
async function onRemoveEntry(id) {
  const gone = wantPage.entries.find((e) => e.id === id);
  if (!gone) return;
  const mine = session;
  wantPage.entries = wantPage.entries.filter((e) => e.id !== id);
  const res = await removeWantEntry(id);
  if (!res.ok && mine === session && !wantPage.entries.some((e) => e.id === id)) {
    wantPage.entries = [...wantPage.entries, gone];
  }
}

/**
 * Register a deck/cube link as a Project and land on it (registration
 * materializes its cards, so the list is reloaded). A refusal says why; a
 * 409 `cap_full` also carries the true Project list and the cap, so the
 * shelf shows the real slots left.
 * @param {string} link
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
async function onAddProject(link) {
  const mine = session;
  const res = await addProject(link);
  if (mine !== session) return { ok: res.ok };
  if (res.ok) {
    await reloadAfterWrite({ pick: res.data?.registration?.binder_id });
    return { ok: true };
  }
  // The failure arm's body is any endpoint's error shape: check it.
  const failed = /** @type {{ projects?: ShelfProject[], project_cap?: number } | undefined} */ (
    res.data
  );
  if (Array.isArray(failed?.projects)) wantPage.projects = failed.projects;
  if (typeof failed?.project_cap === 'number') wantPage.projectCap = failed.project_cap;
  return { ok: false, error: projectAddError(res, wantPage.projectCap) };
}

/**
 * Re-point one want's Card Specificity (the card options), narrowing or
 * broadening, after any re-point of it still being written. The PATCH
 * answers the surviving entry in the read's shape, which takes the old
 * one's place; a re-point onto a specificity the list already holds merges
 * into that entry and the old row goes (the queue maps the old id on, so
 * `survivorOf` finds it in the same update). The answer's holders are not
 * the group picker's, so a scoped page reads again for them. Resolves once
 * this one is written (or refused: the list is left as it was).
 * @param {number} entryId
 * @param {import('../logic/card-options-logic.js').Spec} spec
 * @returns {Promise<void>}
 */
function onSetSpecificity(entryId, spec) {
  const mine = session;
  return queue.enqueue(
    entryId,
    async (id) => {
      const res = await setEntrySpecificity({ id }, spec);
      return res.ok && res.data && mine === session ? res.data : null;
    },
    (id, next) => {
      wantPage.entries = repointEntries(wantPage.entries, id, next);
      if (pickerScope().groupId) reloadAfterWrite();
    }
  );
}

/** @type {import('../logic/want-page-logic.js').OwnerWrites} */
export const ownerWrites = {
  addCard: onAddCard,
  removeEntry: onRemoveEntry,
  setSpecificity: onSetSpecificity,
  addProject: onAddProject,
  removeProject: onRemoveProject,
};
